<script>
    // Notes:
    // - watch issues (see https://github.com/vuejs/petite-vue/discussions/49). Ideally remove all instances of pageChanging and possibly move the onPageChanged function to the mixin.

    import listMixin, { copy, isObject, isValid } from './list-mixin.js';

    export default function(props = {}) {
        const root = this;
        let pageChanging;

        return {
            ...listMixin(props),
            pageSize: props.pageSize ?? 10,
            sortBy: props.sortBy ?? 'name',
            // Exposing the root instance allows you to access the root data within this component.
            get $root() {
                return root;
            },
            get modelName() {
                return props.modelName ?? this.itemsKey;
            },
            get totalPages() {
                return Math.ceil(this._filteredItems.length / this.pageSize);
            },
            get _filteredItems() {
                let items = this[this.itemsKey];

                // Apply the filter.
                for (const key in this.filter) {
                    items = items.filter(i => Object.keys(i).some(k => i[k].toUpperCase().includes(this.filter[key].toUpperCase())));
                }

                return items;
            },
            get filteredItems() {
                let items = this._filteredItems;

                // You must sort before paging.
                if (this.enableSorting) {
                    const order = this.sortDirection == 'Ascending' ? 1 : -1;

                    items.sort((a, b) => {
                        a = a[this.sortBy];
                        b = b[this.sortBy];

                        return (a === b ? 0 : a > b ? 1 : -1) * order;
                    });
                }

                if (this.enablePaging) {
                    // Make sure the page is reset unless we are actually changing the page.
                    if (pageChanging === false)
                        this.page = 1;

                    items = items.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
                }

                return items;
            },
            mounted(element, data = this) {
                // Add the submit data event handler.
                element.closest('form')?.addEventListener('valid', this.submitData);

                // Call the root mounted method.
                root.mounted(element, data);
            },
            unmounted(element, data = this) {
                // Remove the event handler. This make sure when the list is unmounted it's data is not submitted as this would override a new list with the same model name.
                element.closest('form')?.removeEventListener('valid', this.submitData);

                // Call the root unmounted method.
                root.unmounted(element, data);
            },
            onPageChanged(e) {
                pageChanging = true;
        
                // Set the page.
                this.page = e.detail;
        
                this.$nextTick(() => pageChanging = false);
            },
            submitData(e) {
                submitData(e.target, { [this.modelName]: this[this.itemsKey] });
            },
            add(item, e = event) {
                // Validate the form.
                if (!isValid(e.target))
                    return;

                // Trigger the adding event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:adding', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

                // Add the item if the default has not been prevented. Convert the item to a standard object (no observers).
                if (!cancelled) {
                    // Create a copy of the item (since we don't want the reset to modify it).
                    const addedItem = copy(item);

                    // Add the copied item.
                    this[this.itemsKey].push(addedItem);

                    // Trigger the added event.
                    this.$refs.root.dispatchEvent(new CustomEvent('list:added', { bubbles: this.eventBubbles, detail: { list: this, item: addedItem, event: e } }));

                    // Reset the form.
                    this.reset(item, e);
                }
            },
            update(item, e = event) {
                // Validate the form.
                if (!isValid(e.target))
                    return;

                // Trigger the updating event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:updating', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

                // Update the item if the default has not been prevented.
                if (!cancelled) {
                    // Trigger the updated event.
                    this.$refs.root.dispatchEvent(new CustomEvent('list:updated', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));

                    // Set the item we are editing to none.
                    this[this.editItemKey] = null;

                    // Trigger the updated view updated event. Wrapping in $nextTick will make sure it has switched to the item template.
                    this.$nextTick(() => this.$refs.root.dispatchEvent(new CustomEvent('list:updated:viewUpdated', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } })));
                }
            },
            remove(item, e = event) {
                // Trigger the removing event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:removing', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

                // Remove the item if the default has not been prevented.
                if (!cancelled) {
                    // Remove the item.
                    this[this.itemsKey].splice(this[this.itemsKey].indexOf(item), 1);

                    // Trigger the removed event.
                    this.$refs.root.dispatchEvent(new CustomEvent('list:removed', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));
                }
            }
        };
    }

    export function submitData(form, data, prefix = '') {
        for (const key in data) {
            const name = `${prefix}${key}`;
            const value = data[key];

            if (Array.isArray(value))
                value.forEach((v, i) => submitData(form, v, `${name}[${i}].`));
            else if (isObject(value))
                submitData(form, value, `${name}.`);
            else {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                input.value = value;
                form.appendChild(input);
            }
        }
    }
</script>